import React from "react";
import { useTranslation } from "react-i18next";
import styled, { css } from "styled-components";
import moment from "moment";
import { Link } from "@mui/material";

import { Divider } from "js/Components/Divider";
import { Gap10 } from "js/react/components/Gap";

import { BodySmall } from "../FormStyling";
import { calculatePriceWithPromo, formatPrice } from "../../utils/prices";
import type { PlanSummaryProps } from "./PlanSummary";

const RowFlex = css`
    display: flex;
    flex-direction: row;
`;

const Row = styled.div`
    ${RowFlex};
`;

const PriceComparisonContainer = styled.div`
    display: flex;
    flex-direction: column;
`;

const PriceComparisonRow = styled.div`
    ${RowFlex};
    justify-content: space-between;
`;

const AmountPaidContainer = styled.div`
    ${RowFlex};
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
`;

const AmountSavedContainer = styled.div`
    ${RowFlex};
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
`;

const StrikedAmount = styled.span`
    font-family: "Source Sans Pro";
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%;
    letter-spacing: -0.18px;
    text-decoration-line: line-through;
    color: #666;
`;

const Badge = styled.div`
    background: #96BB47;
    color: white;
    padding: 3px 6px;
    display: flex;
    gap: 10px;
    align-items: flex-start;
    text-transform: uppercase;
    border-radius: 1px;
    font-family: "Source Sans Pro";
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 160%;
    letter-spacing: -0.28px;
`;

const StyledLink = styled(Link)`
    &&& {
        font-family: "Source Sans Pro";
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 145%;
        letter-spacing: -0.28px;
        text-decoration-line: underline;
        color: #11a9e2;
    }
`;

export function PlanSummary({ isTrial, billingInterval, productDisplayName, prices, promo, availableReferralCredit, taxAmount, handleBillingIntervalChange }: PlanSummaryProps) {
    const { t } = useTranslation();
    const isAnnual = billingInterval === "year";

    const currentPrice = prices[billingInterval].amount / 100;
    const currentPromoAppliedPrice = calculatePriceWithPromo(currentPrice, promo);

    const annualCostAtMonthlyRate = prices.month.amount * 12 / 100;
    const annualCostAtAnnualRate = prices.year.amount / 100;
    const annualSavingsPercentage = (1 - annualCostAtAnnualRate / annualCostAtMonthlyRate) * 100;

    const dueToday = isTrial ? 0 : Math.max(currentPromoAppliedPrice - (availableReferralCredit / 100), 0);
    const dueNextBillingCycle = currentPromoAppliedPrice;

    const trialExpirationDate = moment().add(14, "days").format("MMMM Do, YYYY");
    const nextBillingDate = isTrial ? trialExpirationDate : isAnnual
        ? moment().add(1, "year").format("MMMM Do, YYYY")
        : moment().add(1, "month").format("MMMM Do, YYYY");

    const taxString = taxAmount ? t(" plus tax") : "";

    return (<>
        <PriceComparisonContainer>
            <PriceComparisonRow>
                <BodySmall>
                    {t(isAnnual ? "{{productDisplayName}} Plan" : "Monthly {{productDisplayName}} Plan", { productDisplayName })}
                </BodySmall>
                <AmountPaidContainer>
                    {isAnnual && <StrikedAmount> {formatPrice(annualCostAtMonthlyRate)}</StrikedAmount>}
                    <BodySmall style={{ fontWeight: 400 }}>
                        {formatPrice(currentPromoAppliedPrice)} / {t(billingInterval)}
                    </BodySmall>
                </AmountPaidContainer>
            </PriceComparisonRow>
            <PriceComparisonRow>
                <AmountSavedContainer>
                    {isAnnual && <StrikedAmount>{formatPrice(annualCostAtMonthlyRate / 12)}</StrikedAmount>}
                    <BodySmall style={{ fontWeight: 400 }}>
                        {isAnnual
                            ? t("{{price}} x {{count}} months", { price: formatPrice(annualCostAtAnnualRate / 12), count: 12 })
                            : t("{{price}} x {{count}} month", { price: formatPrice(annualCostAtMonthlyRate / 12), count: 1 })}
                    </BodySmall>
                </AmountSavedContainer>
                {isAnnual && <Badge>{t("save {{percent}}%", { percent: annualSavingsPercentage.toFixed(0) })}</Badge>}
            </PriceComparisonRow>
        </PriceComparisonContainer>
        {/* @ts-ignore */}
        <Divider margin={15} color="#CCC" />
        <PriceComparisonContainer>
            <PriceComparisonRow>
                <BodySmall>
                    {t("Due today")}
                </BodySmall>
                <BodySmall>
                    {formatPrice(dueToday)}{dueToday > 0 ? taxString : ""}
                </BodySmall>
            </PriceComparisonRow>
            <PriceComparisonRow>
                <BodySmall style={{ fontWeight: 400, color: "#666" }}>
                    {t("Due {{date}}", { date: nextBillingDate })}
                </BodySmall>
                <BodySmall style={{ fontWeight: 400, color: "#666" }}>
                    {formatPrice(dueNextBillingCycle)}{dueNextBillingCycle > 0 ? taxString : ""}
                </BodySmall>
            </PriceComparisonRow>
        </PriceComparisonContainer>

        {!isAnnual &&
            <>
                <Gap10 />
                <Row>
                    <StyledLink
                        // @ts-ignore
                        component="button"
                        variant="body2"
                        onClick={() => handleBillingIntervalChange("year")}
                    >
                        {t("Save {{percent}}% with annual billing", { percent: annualSavingsPercentage.toFixed(0) })}
                    </StyledLink>
                </Row>
            </>
        }
    </>);
}
